import { useCallback, useRef } from 'react';
import type { Subscription } from 'rxjs/Subscription';
import { useFlagsService } from '@atlassian/jira-flags';
import usePortalGroupsResource from '@atlassian/jira-servicedesk-request-types-config-common/src/controllers/portal-groups/index.tsx';
import { UPDATE_GROUP } from '../../common/ui/flags/constants.tsx';
import getErrorFlag from '../../common/ui/flags/main.tsx';
import { useActionsProvider } from '../../services/store';
import useTracking from '../tracking';

const useUpdateGroup = (groupId: number) => {
	const { updateGroup } = useActionsProvider();
	const pendingSubscriptionRef = useRef<Subscription>();
	const { fireError, fireTrack } = useTracking();
	const { showFlag } = useFlagsService();
	const { clear: clearPortalGroupsResourceCache } = usePortalGroupsResource();

	return useCallback(
		(newName: string) => {
			// Don't update on empty name
			if (!newName) {
				return;
			}

			if (pendingSubscriptionRef.current) {
				pendingSubscriptionRef.current.unsubscribe();
			}

			pendingSubscriptionRef.current = updateGroup(groupId, newName, (_, error) => {
				pendingSubscriptionRef.current = undefined;

				if (error) {
					showFlag(getErrorFlag(UPDATE_GROUP, error));
					fireError('updateGroup', error);
				} else {
					fireTrack('portalGroup updated', { groupId });

					clearPortalGroupsResourceCache();
				}
			});
		},
		[clearPortalGroupsResourceCache, fireError, fireTrack, groupId, showFlag, updateGroup],
	);
};

export default useUpdateGroup;
